import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect } from "react"

import type { StandardResponse_UserWithPermissionsResponse_ } from "@/client/types.gen"
import { api } from "@/services/api"
import { useAuthStore } from "@/stores/auth"
import { Scopes } from "@/types/auth"
import type { User } from "@/types/auth"
import { extractScopesFromToken, isTokenExpired } from "@/utils/jwt"

export const useUserWithPermissions = () => {
  const queryClient = useQueryClient()
  const {
    user,
    scopes,
    isAuthenticated,
    accessToken,
    setUser,
    setScopes,
    hasScope,
    hasAnyScope,
    hasAllScopes,
    isAdmin,
    clearAuth,
  } = useAuthStore()

  const enhancedLogout = useCallback(async () => {
    queryClient.clear()
    clearAuth()
  }, [queryClient, clearAuth])

  const {
    data: userResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["current-user", accessToken],
    queryFn: async () => {
      const response = await api.auth.getCurrentUserApiV1AuthMeGet()
      return response as StandardResponse_UserWithPermissionsResponse_
    },
    enabled: !!accessToken && isAuthenticated,
    staleTime: 1 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false
      }
      return failureCount < 3
    },
  })

  useEffect(() => {
    if (!userResponse?.data) {
      return
    }

    const userData = userResponse.data
    const processedUserData = {
      ...userData,
      is_active: userData.is_active ?? true,
    } as User

    const hasChanged =
      !user ||
      user.id !== processedUserData.id ||
      user.updated_at !== processedUserData.updated_at ||
      user.is_active !== processedUserData.is_active

    if (hasChanged) {
      setUser(processedUserData)
    }
  }, [userResponse, user, setUser])

  useEffect(() => {
    if (!accessToken || isTokenExpired(accessToken)) {
      return
    }

    const tokenScopes = extractScopesFromToken(accessToken)
    const scopesDiffer =
      tokenScopes.length !== scopes.length ||
      tokenScopes.some((scope, index) => scope !== scopes[index])

    if (scopesDiffer) {
      setScopes(tokenScopes)
    }
  }, [accessToken, scopes, setScopes])

  useEffect(() => {
    if (error && (error as any)?.status === 401) {
      enhancedLogout()
    }
  }, [error, enhancedLogout])

  return {
    user: user || userResponse?.data || null,
    scopes,
    isLoading,
    error,
    isAuthenticated,
    refetch,
    logout: enhancedLogout,
    hasScope,
    hasAnyScope,
    hasAllScopes,
    isAdmin,
    hasPermission: hasScope,
    hasAnyPermission: hasAnyScope,
    hasAllPermissions: hasAllScopes,
    permissions: scopes,
    canReadUsers: hasScope(Scopes.USERS_READ),
    canUpdateUsers: hasScope(Scopes.USERS_UPDATE),
    canCreateUsers: hasScope(Scopes.USERS_CREATE),
    canDeleteUsers: hasScope(Scopes.USERS_DELETE),
    canReadFiles: hasScope(Scopes.FILES_READ),
    canUpdateFiles: hasScope(Scopes.FILES_UPDATE),
    canCreateFiles: hasScope(Scopes.FILES_CREATE),
    canDeleteFiles: hasScope(Scopes.FILES_DELETE),
    canAccessAdmin: isAdmin() || hasScope(Scopes.SYSTEM_ADMIN),
    canManageSystem: hasScope(Scopes.SYSTEM_ADMIN),
    isUserManager: hasAnyScope([Scopes.USERS_UPDATE, Scopes.USERS_DELETE]),
    isFileManager: hasAnyScope([Scopes.FILES_UPDATE, Scopes.FILES_DELETE]),
  }
}
